import { IApiService, IStockService, StockFilter, StockOrderBy, OrderByMode } from '@/interfaces/Services';
import { inject, injectable } from 'inversify';
import { StockItemModel, StockItemStatsModel, StockCreationModel, StockBatchStatusUpdateModel } from '@/models/stock';
import DependencyIdentifiers from '@/ioc/DependencyIdentifiers';
import { serializeMap } from '@/utils/transformers';

export type PagedStockResponse = {
  data: StockItemModel[],
  meta: { total: number; }
};

export type PagedStockItemStatsModel = {
  data: StockItemStatsModel[],
  meta: { total: number; }
};

@injectable()
export default class StockService implements IStockService {
  private _api: IApiService;

  constructor(@inject(DependencyIdentifiers.ApiService) api: IApiService) {
    this._api = api;
  }

  async getMany(
    page: number = 1,
    size: number = 20,
    filter: StockFilter,
    orderBy?: Map<StockOrderBy, OrderByMode>,
  ): Promise<[StockItemModel[], number]> {
    let query = `/stocks?size=${size}&page=${page}`;
    query += this.getFilterQueryParams(filter);
    if (orderBy && orderBy.size > 0) {
      query += `&orderBy=${this.serializeOrderBy(orderBy)}`;
    }

    const res = await this._api.get<PagedStockResponse>(query);
    return [res.data, res.meta.total];
  }

  async stats(
    page: number = 1,
    size: number = 20,
    filter: StockFilter = {},
    groupByLotId: boolean = false
  ): Promise<[StockItemStatsModel[], number]> {
    let query = `/stocks/stats?size=${size}&page=${page}`
      + `${this.getFilterQueryParams(filter)}&groupByLotId=${(groupByLotId?1:0)}`;

    return await this._api.get<PagedStockItemStatsModel>(query)
      .then(res => [res.data, res.meta.total]);
  }

  async create(data: StockCreationModel): Promise<StockItemModel[]> {
    if (data.amount) { data.amount = parseInt(`${data.amount}`, 10); }
    return await this._api
      .post<{data: StockItemModel[]}>(`/stocks`, { data })
      .then(res => res.data);
  }

  async batchUpdate(data: StockBatchStatusUpdateModel[]): Promise<void> {
    return await this._api
      .post<void>(`/stocks/batch`, { data });
  }

  async return(id: string): Promise<StockItemModel> {
    return await this._api
      .post<{data: StockItemModel}>(`/stocks/${id}/return`, {})
      .then(res => res.data);
  }

  private getFilterQueryParams(filter: StockFilter): string {
    let query = '';
    if (!!filter.patientId) {
      query += `&filter[patientId]=${filter.patientId}`;
    }

    if (!!filter.drugId) {
      query += `&filter[drugId]=${filter.drugId}`;
    }

    if (!!filter.statuses) {
      query += `&filter[statuses]=${filter.statuses.join(',')}`;
    }

    return query;
  }

  private serializeOrderBy(orderBy: Map<StockOrderBy, OrderByMode>) {
    return serializeMap(orderBy);
  }
}
